import { FC, useEffect, useState, useCallback, useRef } from "react";
import { IFurniture } from "../../../interfaces/IFurniture";
import furnitureController, { IEditModuleDimensionsDto } from "../../../Api/furnitureController";
import { useToast } from "../../../hooks/useToast";
import {
    Box,
    Typography,
    Grid,
    Card,
    CardMedia,
    CardContent,
    Paper,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    TextField,
    Button,
    CircularProgress
} from "@mui/material";
import { useUser } from "../../../hooks/useUser";
import { baseURL } from "../../../config";

const BASE_URL = baseURL;

interface ModuleDimensionsEditorProps { }

const ModuleDimensionsEditor: FC<ModuleDimensionsEditorProps> = () => {
    const [furniture, setFurniture] = useState<IFurniture[]>([]);
    const [loading, setLoading] = useState(true);
    const [widths, setWidths] = useState<{ [moduleId: string]: string }>({});
    const [depths, setDepths] = useState<{ [moduleId: string]: string }>({});
    const [heights, setHeights] = useState<{ [moduleId: string]: string }>({});
    const [savedModules, setSavedModules] = useState<{ [moduleId: string]: boolean }>({});
    const toast = useRef(useToast()).current;
    const user = useUser();

    // Przechowuj oryginalne wymiary do przywracania w razie błędu
    const originalDimensions = useRef<{
        [moduleId: string]: { width: string; depth: string; height: string }
    }>({});

    const getFurniture = useCallback(async () => {
        setLoading(true);
        const res = await furnitureController.getFurnitureWithModulesMissingWidthOrDepth();
        if (!res.ok) {
            setLoading(false);
            return toast.error(res.message);
        }
        if (!res.furniture) {
            setLoading(false);
            return toast.error("Nie udało się pobrać modułów");
        }
        setFurniture(res.furniture);

        // Ustaw domyślne wartości inputów na podstawie danych z backendu
        const initialWidths: { [moduleId: string]: string } = {};
        const initialDepths: { [moduleId: string]: string } = {};
        const initialHeights: { [moduleId: string]: string } = {};
        const originals: { [moduleId: string]: { width: string; depth: string; height: string } } = {};
        const initialSaved: { [moduleId: string]: boolean } = {};
        res.furniture.forEach(f =>
            (f.modules || []).forEach(m => {
                initialWidths[m._id] = m.width ? String(m.width) : "";
                initialDepths[m._id] = m.depth ? String(m.depth) : "";
                initialHeights[m._id] = m.height ? String(m.height) : "";
                originals[m._id] = {
                    width: initialWidths[m._id],
                    depth: initialDepths[m._id],
                    height: initialHeights[m._id],
                };
                // Ustaw na true jeśli width i depth są ustawione i nie są zerem
                initialSaved[m._id] =
                    !!m.width && !!m.depth && Number(m.width) > 0 && Number(m.depth) > 0;
            })
        );
        setWidths(initialWidths);
        setDepths(initialDepths);
        setHeights(initialHeights);
        setSavedModules(initialSaved);
        originalDimensions.current = originals;
        setLoading(false);
    }, [toast]);

    useEffect(() => {
        getFurniture();
    }, [getFurniture]);

    const handleWidthChange = (moduleId: string, value: string) => {
        setWidths(prev => ({
            ...prev,
            [moduleId]: value
        }));
        setSavedModules(prev => ({
            ...prev,
            [moduleId]: false
        }));
    };

    const handleDepthChange = (moduleId: string, value: string) => {
        setDepths(prev => ({
            ...prev,
            [moduleId]: value
        }));
        setSavedModules(prev => ({
            ...prev,
            [moduleId]: false
        }));
    };

    const handleHeightChange = (moduleId: string, value: string) => {
        setHeights(prev => ({
            ...prev,
            [moduleId]: value
        }));
        setSavedModules(prev => ({
            ...prev,
            [moduleId]: false
        }));
    };

    const handleModuleEnter = async (moduleId: string) => {
        // Znajdź mebel i moduł
        let foundFurniture: IFurniture | undefined;
        let foundModule: any;
        for (const f of furniture) {
            foundModule = (f.modules || []).find(m => m._id === moduleId);
            if (foundModule) {
                foundFurniture = f;
                break;
            }
        }
        if (!foundFurniture || !foundModule) return toast.error("Nie znaleziono modułu");

        // Przygotuj dane do wysłania
        const width = widths[moduleId] !== "" ? Number(widths[moduleId]) : undefined;
        const depth = depths[moduleId] !== "" ? Number(depths[moduleId]) : undefined;
        const height = heights[moduleId] !== "" ? Number(heights[moduleId]) : undefined;

        // Zapisz aktualne wartości do ewentualnego przywrócenia
        const prevWidth = originalDimensions.current[moduleId]?.width ?? "";
        const prevDepth = originalDimensions.current[moduleId]?.depth ?? "";
        const prevHeight = originalDimensions.current[moduleId]?.height ?? "";

        // Wyślij PATCH
        const dto: IEditModuleDimensionsDto = {
            furnitureId: foundFurniture._id,
            moduleId,
            width,
            depth,
            height
        };

        const res = await furnitureController.editModuleDimensions(dto);

        if (res.ok) {
            toast.success(res.message);
            // Zaktualizuj oryginalne wartości
            originalDimensions.current[moduleId] = {
                width: widths[moduleId],
                depth: depths[moduleId],
                height: heights[moduleId],
            };
            // Ustaw na zielono jeśli width i depth są ustawione i > 0
            setSavedModules(prev => ({
                ...prev,
                [moduleId]:
                    !!width && !!depth && Number(width) > 0 && Number(depth) > 0
            }));
        } else {
            toast.error(res.message);
            // Przywróć stare wartości
            setWidths(prev => ({ ...prev, [moduleId]: prevWidth }));
            setDepths(prev => ({ ...prev, [moduleId]: prevDepth }));
            setHeights(prev => ({ ...prev, [moduleId]: prevHeight }));
            setSavedModules(prev => ({
                ...prev,
                [moduleId]: false
            }));
        }
    };


    if (!user.isAuth) return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
            <Paper sx={{ p: 4, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Typography variant="h5" sx={{ mb: 3 }}>
                    BRAK DOSTĘPU
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => window.location.href = "/logowanie"}
                >
                    Zaloguj się
                </Button>
            </Paper>
        </Box>
    )

    if (loading) return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 280 }}>
            <CircularProgress />
        </Box>
    );

    if (furniture.length === 0) return (
        <Paper variant="outlined" sx={{ p: 5, textAlign: "center", borderRadius: 3, bgcolor: "#f8fafc" }}>
            <Typography variant="h5" fontWeight={800} color="success.main">
                Brak pustych wymiarów
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
                Wszystkie moduły mają uzupełnioną szerokość i głębokość.
            </Typography>
        </Paper>
    );

    return (
        <Box sx={{ p: 3 }}>
            {furniture.map((f, index) => (
                <Paper key={index} elevation={3} sx={{ mb: 4, p: 2 }}>
                    <Grid container spacing={3}>
                        {/* Left: Image and name */}
                        <Grid item xs={12} md={3} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <Card sx={{ width: "100%", maxWidth: 220, mb: 2 }}>
                                <CardMedia
                                    component="img"
                                    height="180"
                                    image={BASE_URL + f.images[0]}
                                    alt={f.name}
                                    sx={{ objectFit: "contain", background: "#fafafa" }}
                                />
                                <CardContent sx={{ p: 1 }}>
                                    <Typography variant="h6" align="center" fontWeight={700}>
                                        {f.name}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        {/* Right: Modules list */}
                        <Grid item xs={12} md={9}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                                Moduły:
                            </Typography>
                            <List>
                                {(f.modules || []).map((mod, modIdx) => {
                                    const isSaved =
                                        savedModules[mod._id] &&
                                        widths[mod._id] !== "" &&
                                        depths[mod._id] !== "" &&
                                        Number(widths[mod._id]) > 0 &&
                                        Number(depths[mod._id]) > 0;
                                    return (
                                        <ListItem
                                            key={mod._id || modIdx}
                                            alignItems="flex-start"
                                            sx={{
                                                mb: 2,
                                                borderRadius: 2,
                                                boxShadow: 1,
                                                bgcolor: isSaved ? "#c8e6c9" : "#fff",
                                                cursor: "pointer",
                                                transition: "background 0.2s",
                                            }}
                                        >
                                            <ListItemAvatar sx={{ minWidth: 0, mr: 2 }}>
                                                <img
                                                    src={BASE_URL + mod.image}
                                                    alt={mod.name}
                                                    style={{
                                                        width: 120,
                                                        height: 120,
                                                        objectFit: "contain",
                                                        background: "#fff",
                                                        borderRadius: 8,
                                                        display: "block",
                                                    }}
                                                />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="subtitle1" fontWeight={600}>
                                                        {mod.name}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
                                                        {/* Opis modułu multiline */}
                                                        {mod.description && (
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    whiteSpace: "pre-wrap",
                                                                    color: "#555",
                                                                    mb: 1,
                                                                }}
                                                            >
                                                                {mod.description}
                                                            </Typography>
                                                        )}
                                                        <Box sx={{ display: "flex", gap: 2 }}>
                                                            <TextField
                                                                label="Szerokość (cm)"
                                                                size="small"
                                                                type="number"
                                                                value={widths[mod._id] ?? ""}
                                                                onChange={e =>
                                                                    handleWidthChange(mod._id, e.target.value)
                                                                }
                                                                sx={{ width: 110 }}
                                                                onKeyDown={e => {
                                                                    if (e.key === "Tab") handleModuleEnter(mod._id);
                                                                }}
                                                                onWheel={e => (e.target as HTMLInputElement).blur()}
                                                            />
                                                            <TextField
                                                                label="Głębokość (cm)"
                                                                size="small"
                                                                type="number"
                                                                value={depths[mod._id] ?? ""}
                                                                onChange={e =>
                                                                    handleDepthChange(mod._id, e.target.value)
                                                                }
                                                                sx={{ width: 120 }}
                                                                onKeyDown={e => {
                                                                    if (e.key === "Tab") handleModuleEnter(mod._id);
                                                                }}
                                                                onWheel={e => (e.target as HTMLInputElement).blur()}
                                                            />
                                                            <TextField
                                                                label="Wysokość (cm)"
                                                                size="small"
                                                                type="number"
                                                                value={heights[mod._id] ?? ""}
                                                                onChange={e =>
                                                                    handleHeightChange(mod._id, e.target.value)
                                                                }
                                                                sx={{ width: 110 }}
                                                                onKeyDown={e => {
                                                                    if (e.key === "Tab") handleModuleEnter(mod._id);
                                                                }}
                                                                onWheel={e => (e.target as HTMLInputElement).blur()}
                                                            />
                                                        </Box>
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    );
                                })}
                                {(f.modules?.length === 0 || !f.modules) && (
                                    <ListItem>
                                        <ListItemText primary={<Typography color="text.secondary">Brak modułów do wyświetlenia.</Typography>} />
                                    </ListItem>
                                )}
                            </List>
                        </Grid>
                    </Grid>
                </Paper>
            ))}
        </Box>
    );
};

export default ModuleDimensionsEditor;
